import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Bell, User as UserIcon, LogOut, Shield, Zap, Pencil, ChevronRight, Trash2, FileText, ScrollText, Building2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useWebPush } from "../hooks/useWebPush";
import { isCapacitorIOS } from "../lib/capacitorEnv";
import { PageHeader } from "../components/Layout";
import { Button, Field } from "../components/UIKit";
import { api } from "../api/client";

function smartInitial(name, email) {
  const pick = (raw) => {
    if (!raw) return null;
    for (const ch of raw.trim()) {
      if (/[a-zA-Z0-9]/.test(ch)) return ch.toUpperCase();
    }
    return null;
  };
  return pick(name) || pick(email) || "?";
}

export default function Profile() {
  const navigate = useNavigate();
  const { user, signOut, updateProfile } = useAuth();
  const push = useWebPush();

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState(null);
  const [emailEnabled, setEmailEnabled] = useState(user?.email_digest_enabled !== false);
  const [emailBusy, setEmailBusy] = useState(false);
  // Trash counter — a single lightweight call to /vehicles/deleted returns
  // {count, vehicles}. The badge stays hidden while loading or when empty.
  const [trashCount, setTrashCount] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/vehicles/deleted");
        if (!cancelled) setTrashCount(data?.count ?? 0);
      } catch {
        if (!cancelled) setTrashCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const displayName = (user?.name || "").trim();
  const initial = useMemo(() => smartInitial(user?.name, user?.email), [user?.name, user?.email]);

  const onSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  const openEdit = () => {
    setEditName(displayName);
    setEditError(null);
    setEditing(true);
  };

  const saveName = async () => {
    setEditError(null);
    const trimmed = editName.trim();
    if (!trimmed) return setEditError("Le nom ne peut pas être vide");
    setEditBusy(true);
    try {
      await updateProfile(trimmed);
      toast.success("Nom mis à jour");
      setEditing(false);
    } catch (e) {
      setEditError(e?.response?.data?.detail || "Mise à jour impossible");
    } finally {
      setEditBusy(false);
    }
  };

  const toggleEmailDigest = async () => {
    if (emailBusy) return;
    const next = !emailEnabled;
    setEmailBusy(true);
    setEmailEnabled(next);
    try {
      await api.patch("/notifications/prefs", { email_digest_enabled: next });
      toast.success(next ? "Récapitulatif email activé" : "Récapitulatif email désactivé");
    } catch {
      setEmailEnabled(!next);
      toast.error("Impossible de mettre à jour la préférence");
    } finally {
      setEmailBusy(false);
    }
  };

  const onEnablePush = async () => {
    const res = await push.subscribe();
    if (res.ok) toast.success("Notifications activées sur cet appareil");
    else if (res.reason === "denied") toast.error("Autorisation refusée. Réactivez-la dans les réglages du navigateur.");
    else if (res.reason === "unavailable" || res.reason === "unsupported") toast.error("Notifications non disponibles sur cet appareil");
    else toast.error("Activation impossible");
  };

  const onDisablePush = async () => {
    const ok = await push.unsubscribe();
    if (ok) toast.success("Notifications désactivées");
    else toast.error("Désactivation impossible");
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      <PageHeader title="Profil" />

      <div style={{ padding: "0 20px 100px" }}>
        {/* User card */}
        <div className="mf-anim-in" data-testid="profile-user-card" style={{
          background: "var(--mf-bg-card)", borderRadius: 20, padding: 28,
          boxShadow: "var(--mf-shadow)", textAlign: "center", marginBottom: 24,
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: 36, margin: "0 auto 16px",
            background: "linear-gradient(135deg, var(--mf-brand), var(--mf-brand-soft))",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontSize: 30, fontWeight: 700, letterSpacing: -0.5,
          }} data-testid="profile-avatar-initial">
            {initial}
          </div>
          <button
            onClick={openEdit}
            data-testid="profile-name-edit"
            style={{
              display: "inline-flex", alignItems: "center", gap: 8, border: "none",
              background: "transparent", cursor: "pointer", padding: 0,
            }}
          >
            <span style={{ fontSize: 20, fontWeight: 700, color: "var(--mf-text)", letterSpacing: -0.2 }}>
              {displayName || "Ajouter votre nom"}
            </span>
            <Pencil size={14} color="var(--mf-text-muted)" />
          </button>
          <div style={{ fontSize: 14, color: "var(--mf-text-secondary)", marginTop: 4 }}>
            {user?.email}
          </div>
        </div>

        {/* Notifications section */}
        <Section title="Notifications">
          <Row
            icon={<Mail size={16} color="var(--mf-brand)" />}
            label="Récapitulatif email"
            sub="Chaque matin à 08 h si des alertes existent"
            right={
              <Switch
                testID="prefs-email-digest"
                checked={emailEnabled}
                onChange={toggleEmailDigest}
                disabled={emailBusy}
              />
            }
            withBorder
          />
          <PushRow
            supported={push.supported}
            status={push.status}
            busy={push.busy}
            isIosSafari={push.isIosSafari}
            isIosStandalone={push.isIosStandalone}
            isCapacitorIOS={isCapacitorIOS()}
            onEnable={onEnablePush}
            onDisable={onDisablePush}
          />
        </Section>

        {/* Compte section */}
        <Section title="Compte">
          <button onClick={openEdit} data-testid="profile-edit-name-row" style={rowBtnStyle}>
            <Row
              icon={<UserIcon size={16} color="var(--mf-brand)" />}
              label="Nom"
              value={displayName || "Non renseigné"}
              chevron
              withBorder
            />
          </button>
          <Row icon={<Mail size={16} color="var(--mf-brand)" />} label="Email" value={user?.email || "—"} />
        </Section>

        <Section title="À propos">
          <Row icon={<Shield size={16} color="var(--mf-brand)" />} label="Données" value="Privées & isolées" withBorder />
          <Row icon={<Zap size={16} color="var(--mf-brand)" />} label="Version" value="1.0.0" />
        </Section>

        <Section title="Informations">
          <button
            onClick={() => navigate("/privacy")}
            data-testid="profile-legal-privacy-row"
            style={rowBtnStyle}
          >
            <Row
              icon={<Shield size={16} color="var(--mf-brand)" />}
              label="Politique de confidentialité"
              chevron
              withBorder
            />
          </button>
          <button
            onClick={() => navigate("/terms")}
            data-testid="profile-legal-terms-row"
            style={rowBtnStyle}
          >
            <Row
              icon={<ScrollText size={16} color="var(--mf-brand)" />}
              label="Conditions d'utilisation"
              chevron
              withBorder
            />
          </button>
          <button
            onClick={() => navigate("/legal")}
            data-testid="profile-legal-legal-row"
            style={rowBtnStyle}
          >
            <Row
              icon={<Building2 size={16} color="var(--mf-brand)" />}
              label="Mentions légales"
              chevron
              withBorder
            />
          </button>
          <button
            onClick={() => navigate("/delete-account")}
            data-testid="profile-legal-delete-info-row"
            style={rowBtnStyle}
          >
            <Row
              icon={<FileText size={16} color="var(--mf-brand)" />}
              label="Suppression du compte"
              chevron
            />
          </button>
        </Section>

        <Section title="Corbeille">
          <button
            onClick={() => navigate("/profile/deleted-vehicles")}
            data-testid="profile-deleted-vehicles-row"
            style={rowBtnStyle}
          >
            <Row
              icon={<Trash2 size={16} color="var(--mf-brand)" />}
              label="Véhicules supprimés"
              value={trashCount > 0 ? String(trashCount) : undefined}
              chevron
            />
          </button>
        </Section>

        <Section title="Zone dangereuse">
          <button
            onClick={() => navigate("/account/delete")}
            data-testid="profile-delete-account-row"
            style={rowBtnStyle}
          >
            <Row
              icon={<Trash2 size={16} color="#dc2626" />}
              label="Supprimer mon compte"
              sub="Action immédiate et irréversible"
              chevron
            />
          </button>
        </Section>

        <div style={{ padding: "20px 0" }}>
          <Button testID="profile-logout-button" variant="secondary" onClick={onSignOut} icon={<LogOut size={18} />}>
            Se déconnecter
          </Button>
        </div>
      </div>

      {/* Edit name dialog */}
      {editing && (
        <div
          onClick={() => !editBusy && setEditing(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(11,25,44,0.35)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20, zIndex: 100,
          }}
          data-testid="name-edit-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--mf-bg-card)", borderRadius: 20, padding: 24,
              width: "100%", maxWidth: 420, boxShadow: "var(--mf-shadow-strong)",
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 4, fontSize: 20, fontWeight: 800, color: "var(--mf-text)" }}>
              Modifier le nom
            </h3>
            <p style={{ margin: "0 0 16px", color: "var(--mf-text-secondary)", fontSize: 14 }}>
              Comment souhaitez-vous être appelé·e ?
            </p>
            <Field
              testID="name-edit-input"
              label="Nom"
              value={editName}
              onChange={setEditName}
              placeholder="Votre nom"
              error={editError}
            />
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <Button
                testID="name-edit-cancel"
                variant="secondary"
                fullWidth
                onClick={() => setEditing(false)}
                disabled={editBusy}
              >
                Annuler
              </Button>
              <Button
                testID="name-edit-save"
                fullWidth
                onClick={saveName}
                loading={editBusy}
              >
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{
        fontSize: 12, fontWeight: 800, color: "var(--mf-text-secondary)",
        margin: "0 0 10px 4px", textTransform: "uppercase", letterSpacing: 0.6,
      }}>{title}</h3>
      <div style={{ background: "var(--mf-bg-card)", borderRadius: 16, boxShadow: "var(--mf-shadow)", overflow: "hidden" }}>
        {children}
      </div>
    </div>
  );
}

const rowBtnStyle = {
  width: "100%", background: "transparent", border: "none", padding: 0, cursor: "pointer", textAlign: "left",
};

function Row({ icon, label, value, sub, right, chevron, withBorder }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "14px 16px", gap: 12,
      borderBottom: withBorder ? "1px solid var(--mf-divider)" : "none",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
        <span style={{
          width: 32, height: 32, borderRadius: 10,
          background: `rgba(11,25,44,0.07)`,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>{icon}</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: "var(--mf-text)" }}>{label}</div>
          {sub && <div style={{ fontSize: 12, color: "var(--mf-text-muted)", marginTop: 2, lineHeight: 1.4 }}>{sub}</div>}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, maxWidth: "55%", justifyContent: "flex-end" }}>
        {value && (
          <span style={{
            fontSize: 14, color: "var(--mf-text-secondary)", fontWeight: 500,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>{value}</span>
        )}
        {right}
        {chevron && <ChevronRight size={16} color="var(--mf-text-muted)" />}
      </div>
    </div>
  );
}

function Switch({ checked, onChange, disabled, testID }) {
  return (
    <button
      onClick={onChange}
      disabled={disabled}
      data-testid={testID}
      role="switch"
      aria-checked={checked}
      style={{
        width: 46, height: 26, borderRadius: 999,
        background: checked ? "var(--mf-brand)" : "var(--mf-border)",
        border: "none", cursor: disabled ? "not-allowed" : "pointer",
        position: "relative", transition: "background 200ms",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <span style={{
        position: "absolute", top: 3, left: checked ? 23 : 3,
        width: 20, height: 20, borderRadius: 10, background: "#fff",
        transition: "left 200ms",
      }} />
    </button>
  );
}

function PushRow({ supported, status, busy, isIosSafari, isIosStandalone, isCapacitorIOS, onEnable, onDisable }) {
  let sub = "Recevez une alerte lorsqu'une échéance approche.";
  let action = { label: "Activer", onPress: onEnable };
  if (isCapacitorIOS) {
    // Capacitor iOS ships without APNs in this MVP — Web Push (VAPID)
    // does not work inside WKWebView. Show a neutral, forward-looking
    // message instead of "not supported".
    sub = "Les notifications iPhone seront disponibles dans une prochaine mise à jour.";
    action = null;
  } else if (!supported) { sub = "Non disponible sur ce navigateur."; action = null; }
  else if (status === "unavailable") { sub = "Notifications indisponibles pour le moment."; action = null; }
  else if (isIosSafari && !isIosStandalone) {
    sub = "Sur iPhone, ajoutez My Fleet à l'écran d'accueil puis rouvrez-la depuis l'icône pour activer les notifications.";
    action = null;
  } else if (status === "denied") {
    sub = "Autorisation refusée. Réactivez-la dans les réglages du navigateur.";
    action = null;
  } else if (status === "granted-active") {
    sub = "Actives sur cet appareil.";
    action = { label: "Désactiver", onPress: onDisable };
  }

  return (
    <div style={{
      display: "flex", alignItems: "flex-start", justifyContent: "space-between",
      padding: "14px 16px", gap: 12,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
        <span style={{
          width: 32, height: 32, borderRadius: 10,
          background: `rgba(11,25,44,0.07)`,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}><Bell size={16} color="var(--mf-brand)" /></span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 500 }}>Notifications sur cet appareil</div>
          <div style={{ fontSize: 12, color: "var(--mf-text-muted)", marginTop: 2, lineHeight: 1.4 }}>{sub}</div>
        </div>
      </div>
      {action && (
        <button
          onClick={action.onPress}
          disabled={busy}
          data-testid="prefs-push-action"
          style={{
            minHeight: 36, padding: "8px 14px", borderRadius: 999,
            background: "rgba(11,25,44,0.08)", color: "var(--mf-brand)",
            border: "none", cursor: busy ? "not-allowed" : "pointer",
            fontWeight: 600, fontSize: 14, opacity: busy ? 0.6 : 1,
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
